import type { AITool } from '../types';

export const XIAOTIANSHU_TOOLS: AITool[] = [
  {
    type: 'function',
    function: {
      name: 'query',
      description: '查询创作数据。scope=context（默认，推荐优先使用）获取分层创作上下文：当前章节全文 + 人物/势力/设定/物品简要索引 + 章节时间线 + 大纲摘要；数据量大时会截断并返回 truncated 标记。可用 filters.query 按关键词搜索相关实体，或用 scope 指定具体类型 + detailLevel=detailed 获取完整字段。scope=timeline 获取章节时间线；scope=chapter_content 获取指定章节完整正文。所有查询只读，安全无副作用。',
      parameters: {
        type: 'object',
        properties: {
          scope: {
            type: 'string',
            description: '查询范围：context（默认，推荐优先使用）| character | faction | setting | chapter | volume | item | outline | book | timeline | chapter_content',
          },
          filters: {
            type: 'object',
            description: '筛选条件（可选）。scope=timeline 时可传 { volumeId } 限定卷范围；scope=chapter_content 时必传 { id } 指定章节ID（数据库ID，非章节编号）；其他 scope 支持 { id?, name?, title?, category?, volumeId?, limit? }。注意：章节返回结果中 id 是数据库ID，chapter_number 才是章节编号（如第1章、第2章）。',
          },
          detailLevel: {
            type: 'string',
            description: '详细程度：brief（仅ID和名称）、normal（默认，含摘要）、detailed（含完整内容）',
          },
        },
        required: [],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'execute',
      description: '执行创作数据变更（创建/修改/删除）。支持单条或批量操作，所有变更（包括章节内容）统一通过审批面板确认后写入数据库。支持人物、势力、设定、物品、卷、大纲、章节内容的增删改，以及章节创建（target="chapter"）。数据必须符合规范（可通过 help 工具查看），否则调度层将返回错误提示。',
      parameters: {
        type: 'object',
        properties: {
          actions: {
            type: 'object',
            description: '操作列表（必填）。支持两种格式：1) 单对象：{ action:"update", target:"chapter_content", targetId:"<uuid>", data:{content:"正文内容"} }；2) 数组：[{ action:"update", target:"chapter_content", targetId:"<uuid>", data:{content:"正文内容"} }]。每项格式：{ action: "create"|"update"|"delete", target: "character"|"faction"|"setting"|"item"|"volume"|"outline"|"chapter_content"|"chapter", targetId?: string（update/delete时必填）, data?: object（create时必填，update时可选） }。注意：chapter 仅支持 create 操作，用于在卷中创建新章节；targetId 是数据库记录 UUID，不是名称或编号。所有操作会弹出审批标签，用户确认后执行。',
          },
          atomic: {
            type: 'boolean',
            description: '是否原子操作（全部成功或遇到失败立即终止，默认false=失败后继续执行剩余操作）',
          },
          description: {
            type: 'string',
            description: '操作描述（显示在用户确认界面，批量操作时建议填写）',
          },
        },
        required: ['actions'],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'help',
      description: '获取工具使用说明和数据规范文档。无需参数。当不确定数据格式或遇到验证错误时，调用此工具获取完整的工具文档，包含：query 和 execute 的参数说明、所有实体类型的数据字段规范、create/update/delete 的正确格式示例。',
      parameters: {
        type: 'object',
        properties: {},
        required: [],
      },
    },
  },
];

/** Agent 状态栏文案（工具调用中） */
export const TOOL_STATUS_LABELS: Record<string, string> = {
  query: '📖 正在查询创作数据...',
  execute: '⚡ 正在执行创作操作...',
  help: '📚 正在获取帮助文档...',
};

const LEGACY_TOOL_NAME_MAP: Record<string, string> = {
  get_creative_context: 'query',
  query_creative_data: 'query',
  analyze_chapter_content: 'query',
  execute_creative_action: 'execute',
  batch_creative_actions: 'execute',
  sync_editor_and_database: 'execute',
};

export function mapLegacyToolArgs(newName: string, args: Record<string, unknown>): Record<string, unknown> {
  if (newName === 'query') {
    if ('include' in args) {
      return { scope: 'context', detailLevel: args.detailLevel, include: args.include };
    }
    if ('type' in args) {
      return { scope: args.type, filters: args.query, detailLevel: args.detailed ? 'detailed' : 'brief' };
    }
    if ('chapterId' in args && args.chapterId && !('content' in args)) {
      return { scope: 'chapter_content', filters: { id: args.chapterId } };
    }
    if ('checkTypes' in args || 'analyzeDimensions' in args || 'improvementAreas' in args) {
      return { scope: 'context' };
    }
    return args;
  }

  if (newName === 'execute') {
    if ('action' in args && 'target' in args && !('actions' in args)) {
      return {
        actions: [{ action: args.action, target: args.target, targetId: args.targetId, data: args.data }],
        description: (args.context as Record<string, unknown> | undefined)?.reason as string | undefined,
      };
    }
    if ('actions' in args && Array.isArray(args.actions)) {
      return { actions: args.actions, atomic: args.atomic, description: args.description };
    }
    if ('chapterId' in args && 'content' in args) {
      const updates = args.updates as { title?: string; summary?: string } | undefined;
      return {
        actions: [{
          action: 'update', target: 'chapter_content', targetId: args.chapterId,
          data: {
            content: args.content,
            ...(updates?.title ? { title: updates.title } : {}),
            ...(updates?.summary ? { summary: updates.summary } : {}),
          },
        }],
        description: (args.reason as string | undefined) ?? 'ai_update',
      };
    }
    return args;
  }

  return args;
}

export function resolveToolName(toolName: string): string {
  const allowedTools = new Set(XIAOTIANSHU_TOOLS.map((t) => t.function.name));
  if (allowedTools.has(toolName)) return toolName;
  const mapped = LEGACY_TOOL_NAME_MAP[toolName];
  if (mapped) return mapped;
  throw new Error(`未知工具：${toolName}`);
}
